/**
 * File system utilities for opening and saving files.
 * Handles File System Access API with fallback for browsers that don't support it.
 */

/**
 * Open a file picker and return the selected file.
 * @param {Object} options - File picker options
 * @param {boolean} options.multiple - Allow multiple file selection (default: false)
 * @param {Array} options.types - File type restrictions (default: JSON files)
 * @returns {Promise<File>} The selected file
 * @throws {Error} If file selection is cancelled or fails
 */
export async function openFileWithPicker(options = {}) {
  const {
    multiple = false,
    types = [
      {
        description: 'JSON Files',
        accept: {
          'application/json': ['.json']
        }
      }
    ]
  } = options;

  // Check if File System Access API is supported
  if ('showOpenFilePicker' in window) {
    // Use File System Access API for Chrome/Edge
    const fileHandles = await window.showOpenFilePicker({
      multiple,
      types
    });

    const fileHandle = multiple ? fileHandles : fileHandles[0];
    return await (multiple ? Promise.all(fileHandles.map(h => h.getFile())) : fileHandle.getFile());
  } else {
    // Fallback for Firefox/Safari - use regular file input
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = types.flatMap(t => Object.values(t.accept).flat()).join(',');
      input.multiple = multiple;

      input.onchange = async (event) => {
        const files = event.target.files;
        if (files.length === 0) {
          reject(new Error('No file selected'));
          return;
        }

        if (!multiple && files.length !== 1) {
          reject(new Error('Please select a single file'));
          return;
        }

        resolve(multiple ? Array.from(files) : files[0]);
      };

      input.oncancel = () => {
        reject(new Error('File selection cancelled'));
      };

      input.click();
    });
  }
}

/**
 * Save a blob to a file using a save picker.
 * @param {Blob} blob - The data to save
 * @param {string} defaultFilename - Default filename to suggest
 * @param {Object} options - Save picker options
 * @param {string} options.description - File type description (default: 'JSON File')
 * @param {Object} options.accept - MIME type and extensions (default: JSON)
 * @returns {Promise<void>}
 * @throws {Error} If save is cancelled or fails
 */
export async function saveFileWithPicker(blob, defaultFilename, options = {}) {
  const {
    description = 'JSON File',
    accept = {
      'application/json': ['.json']
    }
  } = options;

  // Check if File System Access API is supported
  if ('showSaveFilePicker' in window) {
    // Use File System Access API for Chrome/Edge
    const fileHandle = await window.showSaveFilePicker({
      suggestedName: defaultFilename,
      types: [
        {
          description,
          accept
        }
      ]
    });

    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
  } else {
    // Fallback for Firefox/Safari - use download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = defaultFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
